import { parseISO } from 'date-fns';
import {
    BaseQueryFn,
    FetchArgs,
    FetchBaseQueryError,
    FetchBaseQueryMeta,
    QueryDefinition,
} from '@reduxjs/toolkit/dist/query';
import { LazyQueryTrigger, UseLazyQuery, UseQuery } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { useCreateDialogRequest } from 'components/create-dialog/CreateDialogContext';
import { CountDto } from 'types/count';
import { PaginationProps, QueryResponse } from 'types/paginationAndFilter';
import { warnIfKeywordsTruncated } from 'utils/multiKeywordSearch';
import { isNumeric } from 'utils/number.ts';
import { buildRsqlFromSearchText } from 'utils/searchQueryToRsql';
import {
    CheckBoxGroupFilterForm,
    DropdownFilterForm,
    FilterCheckboxInForm,
    FilterConfig,
    MainFilterForm,
    MainFilterInForm,
    MultiselectFilterForm,
    NOT_NULL_FILTER_SENTINEL,
    NULL_FILTER_SENTINEL,
    PeriodQuickFilter,
    QuickFilterCheckboxGroupForm,
    QuickFilterDropdownMultiselectForm,
    QuickFilterForm,
    QuickFilterFormItem,
    SelectOption,
} from './ListTableTypes';
import { PERIOD_PRESET_PARAM_KEY, presetParamOf } from './periodPreset';
import { dropRuledOutOptions } from './ruledOutOptions';
import { changeCheckBoxGroupFilterState } from './filter-pieces/inputs/CheckboxGroup.tsx';
import { SAVED_VIEW_PARAM_KEY } from './filter-bar/builtInViews';
import {
    compareSearchParams,
    emptyTextClause,
    notComparator,
    serializeFilterParams,
    VIRTUAL_MARKER,
} from './filterUtil.ts';
import { compositeFromArgs } from './compositeFilter';
import { getSelectorKey } from './getSelectorKey';
import { useFilterPersistence } from './useFilterPersistence';

/**
 * A multiselect main filter after its quick control changed: the values the quick control lists come from it, the
 * rest stay. A quick control has no "is not" and ticks none of an excluding filter's values, so it leaves an
 * excluding filter alone until a value is ticked in it, which then replaces the exclusion.
 */
const withQuickSelection = (
    main: MultiselectFilterForm,
    quickSelected: SelectOption[],
    isListedByQuick: (option: SelectOption) => boolean
): MultiselectFilterForm => {
    if (!main.isExcluding) {
        return { ...main, selected: [...main.selected.filter((option) => !isListedByQuick(option)), ...quickSelected] };
    }
    return quickSelected.length > 0 ? { ...main, selected: quickSelected, isExcluding: false } : main;
};

/**
 * Gets the in/out keys and values for multiselect filters, handling both default and custom comparators.
 * For default comparators (=in=), checks the field's `conditions.notComparator` key, else =out=, for exclude mode.
 * For custom comparators, checks for negated version (=notXXX=) for exclude mode.
 */
const getMultiselectFilterValues = (
    group: { selector: string; comperator?: string; conditions?: { notComparator?: string } },
    itemKey: string,
    derivedFilterState: Record<string, string[]> | undefined
): { values: string[]; isExcludingFromQuery: boolean } => {
    const hasDefaultComparator = !group.comperator || group.comperator === '=in=';
    const inKey = itemKey;
    let outKey: string | undefined;
    if (hasDefaultComparator) {
        outKey = itemKey.replace(':=in=', `:${group.conditions?.notComparator ?? '=out='}`);
    } else if (group.comperator) {
        const match = group.comperator.match(/^=(.+)=$/);
        if (match) {
            outKey = `${group.selector}:=not${match[1]}=`;
        }
    }
    const inValues = derivedFilterState?.[inKey] || [];
    const outValues = outKey ? derivedFilterState?.[outKey] || [] : [];
    const values = inValues.length > 0 ? inValues : outValues;
    const isExcludingFromQuery = outValues.length > 0;
    return { values, isExcludingFromQuery };
};

/** The is empty / is not empty option an `emptyComparator` clause reads back as, or none. */
const emptinessOf = (
    group: {
        selector: string;
        emptyComparator?: string;
        nullOption?: { label: string };
        notEmptyOption?: { label: string };
    },
    derivedFilterState: Record<string, string[]> | undefined
): SelectOption[] | undefined => {
    const args = group.emptyComparator ? derivedFilterState?.[`${group.selector}:${group.emptyComparator}`] : undefined;
    if (args?.includes('true')) return [{ value: NOT_NULL_FILTER_SENTINEL, label: group.notEmptyOption?.label ?? '' }];
    if (args?.includes('false')) return [{ value: NULL_FILTER_SENTINEL, label: group.nullOption?.label ?? '' }];
    return undefined;
};

const CLAUSE_OPERATOR = /(==|!=|=[a-zA-Z0-9]+=)/;
const BARE_IS_NULL = /^([^=()]+)=isnull=true$/;

/**
 * One `qf` / `mf` clause into its `selector:comparator` key and values. A bare `selector=isnull=true` is
 * the null option of a multiselect, seeded as the sentinel under `:=in=`, and also kept under its own key
 * for a checkbox group that sends it. `(selector=op=(…),selector=isnull=true)` — an exclusion that keeps
 * the empty values, or values plus the null option — reads as its first clause, the sentinel added for
 * `=in=`.
 */
const readClause = (filter: string): { key: string; values: string[] }[] => {
    const bareNullMatch = filter.match(BARE_IS_NULL);
    if (bareNullMatch) {
        const selector = bareNullMatch[1];
        return [
            { key: `${selector}:=in=`, values: [NULL_FILTER_SENTINEL] },
            { key: `${selector}:=isnull=`, values: ['true'] },
        ];
    }
    const wrapped = filter.startsWith('(') ? filter.match(/^\((.*),.*?=isnull=true\)$/) : null;
    const filterPieces = (wrapped ? wrapped[1] : filter).split(CLAUSE_OPERATOR);
    if (filterPieces.length < 3) return [];
    const values = filterPieces[2].replace(/[()]+/g, '').split(/,(?=(?:[^"]*"[^"]*")*[^"]*$)/); // https://stackoverflow.com/questions/18893390/splitting-on-comma-outside-quotes/18893443#18893443
    if (wrapped && filterPieces[1] === '=in=') {
        values.push(NULL_FILTER_SENTINEL);
    }
    return [{ key: `${filterPieces[0]}:${filterPieces[1]}`, values }];
};

/**
 * Processes custom comparators and applies text transformations
 * Currently supports: =lknumeric= (converts comma to dot, removes trailing zeros)
 */
const processComparator = (
    comparator: string,
    searchText: string
): { actualComparator: string; processedText: string } => {
    if (comparator === '=lknumeric=') {
        let processedText = searchText.replace(/,/g, '.');
        // Remove trailing zeros after decimal point (e.g., "2.80" -> "2.8")
        if (processedText.includes('.')) {
            processedText = processedText.replace(/\.?0+$/, '');
        }
        return {
            actualComparator: '=lk=',
            processedText,
        };
    }
    return {
        actualComparator: comparator,
        processedText: searchText,
    };
};

export type UseListTableFilterProps<DataType, TResponse extends QueryResponse<DataType> = QueryResponse<DataType>> = {
    data?: TResponse;
    rowsPerPage?: number;
    filterConfig?: FilterConfig;
    disableUrlState?: boolean;
    /** Disables all fetch operations. When true, no API calls will be made regardless of user actions or state changes. */
    disableFetch?: boolean;
    /** Disables automatic initial fetch on hook mount. Useful when there are suggestions/data prefetched for the user - if user doesn't choose those, they can initiate fetch by typing in searchbar or other actions like clearFilters, handleApplyFilters, handleNewSearch, etc. */
    disableInitialFetch?: boolean;
    initialSearchParams?: string;
    canRememberFilters?: boolean;
    fetchData: LazyQueryTrigger<
        QueryDefinition<
            PaginationProps,
            BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, Record<string, unknown>, FetchBaseQueryMeta>,
            never,
            TResponse,
            'portalAPI'
        >
    >;
    fetchCount?: UseLazyQuery<
        QueryDefinition<
            Pick<PaginationProps, 'filters'>,
            BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, Record<string, unknown>, FetchBaseQueryMeta>,
            string,
            CountDto,
            'portalAPI'
        >
    >;
    fetchTotals?: UseQuery<
        QueryDefinition<
            Pick<PaginationProps, 'filters'>,
            BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, Record<string, unknown>, FetchBaseQueryMeta>,
            string,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- per-consumer totals query response shape; the hook is endpoint-agnostic and tightening to `unknown` here is rejected as invariant by RTK Query's `UseQuery<QueryDefinition<..., TResult, ...>>`. Consumer specifies the concrete shape at the call site via the TQuery generic on ListTableWithFilters.
            any,
            'portalAPI'
        >
    >;
    forceTotalsVisible?: boolean;
    /**
     * When true, totals are fetched only after the user flips the "Show totals" toggle on.
     * The toggle persists across filter/search changes — the parent is responsible for
     * forcing it off when the dataset exceeds the totals limit. Supersedes
     * `forceTotalsVisible` — if both are passed, the on-demand toggle wins.
     */
    onDemandTotals?: boolean;
    /** Initial value for the on-demand toggle. Useful for views that should default to "totals on". */
    defaultTotalsRowRequested?: boolean;
    onFilterChange?: (activeFilter: string) => void;
    onApplyFilterState?: (state: MainFilterForm) => void;
    /**
     * URL query-param keys this hook doesn't own but should carry forward verbatim when it rewrites
     * the URL (e.g. the My Portfolio `scope`/`userIds`). Without this they'd be clobbered on every
     * filter change; preserving them keeps them shareable and lets `saveFilters` persist them too.
     * `savedView` is always carried, whatever the list passes here.
     */
    preservedParamKeys?: readonly string[];
    /**
     * Sort keys this list can legitimately send (column ids + sortAccessors). Enforced at the
     * fetch boundary in `refetch` — the single point the API request, the URL rewrite and the
     * persisted filter entry all derive their sort from — so a foreign key (e.g. an Invoice-only
     * jsonb sort replayed on the timecards list) can never be sent by this list no
     * matter how it got into the state, and a poisoned localStorage entry self-heals on the next
     * fetch. Sorts restored from the URL or persistence are additionally dropped on their way
     * into `sortState`, keeping the UI state consistent with what is sent. When omitted, sorts
     * pass through unchanged.
     */
    validSortAccessors?: readonly string[];
    /**
     * Rewrites the sort actually sent to the API, given the user's (already sanitized) column sort.
     * For a list whose own state adds sort keys the columns cannot express — a grouped list, whose
     * grouping has to be the PRIMARY sort so the groups arrive contiguous rather than being cut up
     * by the page boundary.
     *
     * Only the request is affected: the URL and the persisted filters still carry the column sort
     * alone, since that is the part the user chose by clicking a header.
     *
     * Must be memoized on whatever it closes over — a change of identity refetches from page 0,
     * which is what makes a new grouping reload the list.
     */
    composeSort?: (columnSort: string) => string | string[];
};

/**
 * Where a starting filter state is read from: the URL (which the remembered filters or the page's defaults seed when
 * it has none), nothing at all, or the page's own default filters.
 */
type ParamSource = 'url' | 'none' | 'defaults';

/** The period quick filter's preset named `value`, with the selector it fills. */
function periodPresetOf(period: PeriodQuickFilter | undefined, value: string | null | undefined) {
    const preset = value ? period?.presets.find((each) => each.value === value) : undefined;
    return period && preset ? { selector: period.selector, range: preset.range } : undefined;
}

const useListTableFilter = <DataType, TResponse extends QueryResponse<DataType> = QueryResponse<DataType>>(
    props: UseListTableFilterProps<DataType, TResponse>
) => {
    const {
        data,
        fetchData,
        fetchCount,
        fetchTotals,
        filterConfig,
        rowsPerPage = 50,
        disableUrlState: disableUrlStateProp = false,
        disableFetch = false,
        disableInitialFetch = false,
        forceTotalsVisible = false,
        onDemandTotals = false,
        defaultTotalsRowRequested = false,
        initialSearchParams,
        canRememberFilters = true,
        onFilterChange,
        onApplyFilterState,
        preservedParamKeys,
        validSortAccessors,
        composeSort,
    } = props;
    const preservedKeys = useMemo(() => [...(preservedParamKeys ?? []), SAVED_VIEW_PARAM_KEY], [preservedParamKeys]);
    const [isInitialFetchDone, setIsInitialFetchDone] = useState(false);
    const [activeFilter, setActiveFilter] = useState<string>('');

    const [currentPageIndex, setCurrentPageIndex] = useState(0);
    const hasInitialFetchStartedRef = useRef(false);
    const isFirstMountRef = useRef(true);
    // Last-seen values of the preserved params (e.g. portfolio scope/userIds). Lets the
    // searchParams-change effect below refetch when one of them changes, even though they don't map
    // to a filter the URL/state comparison knows about. Null until first observed.
    const prevPreservedParamsRef = useRef<string | null>(null);

    const routerLocation = useLocation();
    // In the create dialog the router describes the page behind it. A table there is on the creation's own URL:
    // it starts from its initial params, and neither reads that page's filters nor writes its own into them.
    const createDialogRequest = useCreateDialogRequest();
    const disableUrlState = disableUrlStateProp || createDialogRequest !== null;
    const { state, pathname: locationPathname } = createDialogRequest?.location ?? routerLocation;
    const mountedPathnameRef = useRef(locationPathname);

    const {
        getInitialSearchParams,
        hasClearedFilters,
        isFilterPersistenceEnabled,
        toggleFilterPersistence,
        saveFilters,
        initializeMissingEntry,
        getCustomData,
    } = useFilterPersistence({
        canRememberFilters: canRememberFilters && createDialogRequest === null,
        initialSearchParams,
    });

    const [routerSearchParams, setSearchParams] = useSearchParams(getInitialSearchParams);
    const ownSearchParams = useMemo(() => new URLSearchParams(getInitialSearchParams), [getInitialSearchParams]);
    const searchParams = createDialogRequest ? ownSearchParams : routerSearchParams;
    const [virtualSearchParams, setVirtualSearchParams] = useState<Record<string, string>>(() =>
        Object.fromEntries(new URLSearchParams(getInitialSearchParams))
    );

    // Fallback for pages that opt out of URL writes; only consulted on initial mount with an empty URL.
    const initialSearchParamsParsed = useMemo(
        () => (getInitialSearchParams ? new URLSearchParams(getInitialSearchParams) : null),
        [getInitialSearchParams]
    );
    const shouldUseInitialParamFallback = disableUrlState && searchParams.size === 0;
    const getInitialParamString = useCallback(
        (key: 'mf' | 'qf' | 'search' | 'sort' | typeof PERIOD_PRESET_PARAM_KEY) =>
            searchParams.get(key) ??
            (shouldUseInitialParamFallback ? (initialSearchParamsParsed?.get(key) ?? null) : null),
        [searchParams, initialSearchParamsParsed, shouldUseInitialParamFallback]
    );

    const defaultParams = useMemo(() => new URLSearchParams(initialSearchParams ?? ''), [initialSearchParams]);
    const readParam = (source: ParamSource, key: 'mf' | 'qf' | typeof PERIOD_PRESET_PARAM_KEY) => {
        if (source === 'url') return getInitialParamString(key);
        return source === 'defaults' ? defaultParams.get(key) : null;
    };
    // Remembered cleared filters open the list as Clear left it, not on the default preset an empty URL gets.
    const addressSource: ParamSource = hasClearedFilters ? 'none' : 'url';

    const hasFilters = useMemo(
        () => !!virtualSearchParams?.['mf'] || !!virtualSearchParams?.['qf'] || !!virtualSearchParams?.['search'],
        [virtualSearchParams]
    );

    const quickFilterCount = virtualSearchParams['qf']?.split(';').length ?? 0;
    const mainFilterCount = virtualSearchParams['mf']?.split(';').length ?? 0;

    const isCheckboxGroupQuickFilter = (filter: QuickFilterFormItem): filter is QuickFilterCheckboxGroupForm => {
        return !('type' in filter) || filter.type !== 'dropdown-multiselect';
    };

    const synchronizeQuickFilterState = useCallback((mainFilterState: MainFilterForm) => {
        setQuickFilterState((prevQuickFilterState) => {
            const newQuickFilterState = { ...prevQuickFilterState };
            for (const [_, mainFilter] of Object.entries(mainFilterState)) {
                if (mainFilter && mainFilter.type === 'checkboxgroup') {
                    const checkedMainFilter = mainFilter as CheckBoxGroupFilterForm;
                    const { filters } = checkedMainFilter;
                    const itemKey = getSelectorKey(checkedMainFilter);
                    const quickFilter = newQuickFilterState[itemKey];
                    if (quickFilter && isCheckboxGroupQuickFilter(quickFilter)) {
                        // Paired on the RSQL argument — the two groups list their own subsets of the
                        // values in their own order, and the main filter owns the clause, so a
                        // shortcut left unpaired would sit at odds with the filter that is applied.
                        const mainFilterByArgument = new Map<string, FilterCheckboxInForm>(
                            (filters as FilterCheckboxInForm[]).filter((f) => !f.isNo).map((f) => [f.argument, f])
                        );
                        newQuickFilterState[itemKey] = {
                            ...quickFilter,
                            filters: quickFilter.filters.map((filter) => {
                                const matchingMainFilter = mainFilterByArgument.get(filter.argument);
                                if (matchingMainFilter) {
                                    return { ...filter, isSelected: matchingMainFilter.isSelected };
                                }
                                return filter;
                            }),
                        };
                    }
                }
                if (mainFilter && mainFilter.type === 'multiselect') {
                    const multiselectMainFilter = mainFilter as MultiselectFilterForm;
                    const itemKey = getSelectorKey(multiselectMainFilter);
                    const quickFilter = newQuickFilterState[itemKey];
                    // The quick control has no "is not": it ticks none of the values a filter excludes.
                    const shownSelected = multiselectMainFilter.isExcluding ? [] : multiselectMainFilter.selected;
                    if (quickFilter && isCheckboxGroupQuickFilter(quickFilter)) {
                        // Normalize selected values for comparison (remove quotes, convert to string)
                        const normalizeValue = (val: string) => val.replace(/^"|"$/g, '');
                        const selectedValues = new Set(
                            shownSelected.map((option) => normalizeValue(String(option.value)))
                        );
                        newQuickFilterState[itemKey] = {
                            ...quickFilter,
                            filters: quickFilter.filters.map((filter) => {
                                const filterValue = normalizeValue(String(filter.value || filter.argument));
                                const isSelected = selectedValues.has(filterValue);
                                return { ...filter, isSelected };
                            }),
                        };
                    } else if (quickFilter && !isCheckboxGroupQuickFilter(quickFilter)) {
                        // Handle dropdown-multiselect quick filter synchronization
                        const dropdownQuickFilter = quickFilter as QuickFilterDropdownMultiselectForm;
                        newQuickFilterState[itemKey] = {
                            ...dropdownQuickFilter,
                            selected: shownSelected,
                        };
                    }
                }
            }
            return newQuickFilterState;
        });
    }, []);

    const synchronizeMainFilterStatePure = useCallback(
        (quickFilterState: QuickFilterForm, mainFilterState: MainFilterForm) => {
            const newMainFilterState: MainFilterForm = { ...mainFilterState };
            for (const [_, mainFilter] of Object.entries(mainFilterState)) {
                if (mainFilter && mainFilter.type === 'checkboxgroup') {
                    const checkedMainFilter = mainFilter as CheckBoxGroupFilterForm | undefined;
                    if (checkedMainFilter) {
                        const itemKey = getSelectorKey(checkedMainFilter);
                        const quickFilter = quickFilterState[itemKey];
                        if (quickFilter && isCheckboxGroupQuickFilter(quickFilter)) {
                            // Values the shortcut does not list keep whatever the main control has —
                            // it is the superset, and the shortcut only speaks for its own arguments.
                            const quickFilterByArgument = new Map<string, (typeof quickFilter.filters)[number]>(
                                quickFilter.filters.map((f) => [f.argument, f])
                            );
                            newMainFilterState[itemKey] = {
                                ...checkedMainFilter,
                                filters: ((newMainFilterState[itemKey] as CheckBoxGroupFilterForm)?.filters || []).map(
                                    (filter: FilterCheckboxInForm) => {
                                        const matchingQuickFilter = quickFilterByArgument.get(filter.argument);
                                        if (matchingQuickFilter && !filter.isNo) {
                                            return { ...filter, isSelected: matchingQuickFilter.isSelected };
                                        }
                                        return filter;
                                    }
                                ),
                            };
                        }
                    }
                }
                if (mainFilter && mainFilter.type === 'dropdown') {
                    const itemKey = getSelectorKey(mainFilter);
                    const quickFilterStateElement = quickFilterState[itemKey];
                    const newMainFilterStateElement = newMainFilterState[itemKey] as DropdownFilterForm;
                    if (
                        quickFilterStateElement &&
                        isCheckboxGroupQuickFilter(quickFilterStateElement) &&
                        newMainFilterStateElement
                    ) {
                        const isDeselectedByQuickFilter = quickFilterStateElement.filters.some(
                            (filter) =>
                                filter.argument === newMainFilterStateElement.selected?.value && !filter.isSelected
                        );
                        if (isDeselectedByQuickFilter) {
                            newMainFilterState[itemKey] = { ...newMainFilterStateElement, selected: null };
                        }
                    }
                }
                if (mainFilter && mainFilter.type === 'multiselect') {
                    const itemKey = getSelectorKey(mainFilter);
                    const quickFilterStateElement = quickFilterState[itemKey];
                    const newMainFilterStateElement = newMainFilterState[itemKey] as MultiselectFilterForm;
                    if (
                        quickFilterStateElement &&
                        isCheckboxGroupQuickFilter(quickFilterStateElement) &&
                        newMainFilterStateElement
                    ) {
                        const normalizeValue = (val: string) => val.replace(/^"|"$/g, '');

                        // The quick control only speaks for the values it lists. A "Show done"
                        // toggle beside a full status multiselect governs DONE and nothing else, so
                        // its state decides DONE while every other selected value — and the null
                        // sentinel, which is in no option list — is carried over untouched.
                        // Where the quick control lists every option (the shortcut is a mirror of
                        // the main filter) that leaves nothing to carry over and this reduces to
                        // taking the quick filter's selection wholesale, as it always did.
                        const quickFilterVocabulary = new Set(
                            quickFilterStateElement.filters.map((filter) =>
                                normalizeValue(String(filter.value || filter.argument))
                            )
                        );
                        const quickFilterSelectedValues = new Set(
                            quickFilterStateElement.filters
                                .filter((filter) => filter.isSelected)
                                .map((filter) => normalizeValue(String(filter.value || filter.argument)))
                        );

                        newMainFilterState[itemKey] = withQuickSelection(
                            newMainFilterStateElement,
                            newMainFilterStateElement.options.filter((option) =>
                                quickFilterSelectedValues.has(normalizeValue(String(option.value)))
                            ),
                            (option) => quickFilterVocabulary.has(normalizeValue(String(option.value)))
                        );
                    } else if (
                        quickFilterStateElement &&
                        !isCheckboxGroupQuickFilter(quickFilterStateElement) &&
                        newMainFilterStateElement
                    ) {
                        // Handle dropdown-multiselect quick filter → multiselect main filter synchronization.
                        // Same rule as the checkbox shortcut above: the dropdown decides the options
                        // it offers, anything else the main filter holds (the null sentinel) stays.
                        const dropdownQuickFilter = quickFilterStateElement as QuickFilterDropdownMultiselectForm;
                        const dropdownVocabulary = new Set(
                            dropdownQuickFilter.options.map((option) => String(option.value))
                        );
                        newMainFilterState[itemKey] = withQuickSelection(
                            newMainFilterStateElement,
                            dropdownQuickFilter.selected || [],
                            (option) => dropdownVocabulary.has(String(option.value))
                        );
                    }
                }
            }
            return newMainFilterState;
        },
        []
    );

    const synchronizeMainFilterState = useCallback(
        (quickFilterState: QuickFilterForm) => {
            setMainFilterState((prevMainFilterState) => {
                return synchronizeMainFilterStatePure(quickFilterState, prevMainFilterState);
            });
        },
        [synchronizeMainFilterStatePure]
    );

    const handleQuickFilterStateChange = useCallback(
        (state: QuickFilterForm, shouldSynchronizeMainFilterState: boolean = true) => {
            setQuickFilterState({ ...state });
            if (shouldSynchronizeMainFilterState) {
                synchronizeMainFilterState(state);
            }
        },
        [synchronizeMainFilterState]
    );

    const handleMainFilterStateChange = useCallback(
        (state: MainFilterForm, shouldSynchronizeQuickFilterState: boolean = true) => {
            setMainFilterState({ ...state });
            if (shouldSynchronizeQuickFilterState) {
                synchronizeQuickFilterState(state);
            }
        },
        [synchronizeQuickFilterState]
    );

    const setSearchBarStateCallback = useCallback((value = '') => {
        setSearchBarState(value);
    }, []);

    const scrollTop = useCallback(() => {
        if (window) {
            window.scrollTo(0, 0);
        }
    }, []);

    /**
     * Collects the values a `selector:comparator` key carries. A key can be met more than once —
     * across the `qf` and `mf` params, or twice within one of them (`status=isnull=true` alongside
     * `status=in=(done)`) — and the controls have to come back holding every value the active filter
     * names, so repeats union rather than overwrite each other.
     */
    const addDerivedFilterValues = (state: Record<string, string[]>, key: string, values: string[]) => {
        state[key] = [...new Set([...(state[key] ?? []), ...values])];
    };

    const getQuickSearchDerivedFilterState = (source: ParamSource) => {
        const derivedFilterState = {} as Record<string, string[]>;

        const filterString = readParam(source, 'qf');
        filterString
            ?.split(';')
            .forEach((filter) =>
                readClause(filter).forEach(({ key, values }) => addDerivedFilterValues(derivedFilterState, key, values))
            );

        return derivedFilterState;
    };

    const mergeDerivedFilterState = (state1: Record<string, string[]>, state2: Record<string, string[]>) => {
        const mergedState = { ...state1 };
        Object.keys(state2).forEach((key) => {
            addDerivedFilterValues(mergedState, key, state2[key]);
        });
        return mergedState;
    };

    /**
     * The `mf` param, parsed into `selector:comparator` → values. Read by the main filters and — for
     * a key a quick filter shares — by the quick ones too: a list can seed its default view through
     * either param (`initialSearchParams`), and the shortcut has to reflect whichever one carries it.
     */
    const getMainSearchDerivedFilterState = (source: ParamSource) => {
        const derivedFilterState = {} as Record<string, string[]>;
        const filterString = readParam(source, 'mf');
        const filterArray = filterString ? filterString.split(';') : ([] as string[]);

        filterArray.forEach((filter) =>
            readClause(filter).forEach(({ key, values }) => addDerivedFilterValues(derivedFilterState, key, values))
        );

        return { derivedFilterState, filterArray };
    };

    const getInitialQuickFilters = (source: ParamSource = addressSource) => {
        const newQuickFilterState = {} as QuickFilterForm;
        // `qf` first, `mf` only for the keys it does not carry: on a key both a quick and a main
        // filter target, the clause lives in whichever param the state was restored from, and the
        // shortcut must come back checked whenever the value it stands for is in the active filter.
        const derivedFilterState = mergeDerivedFilterState(
            getQuickSearchDerivedFilterState(source),
            getMainSearchDerivedFilterState(source).derivedFilterState
        );

        filterConfig?.quickFilters?.forEach((group) => {
            const itemKey = `${group.selector}${group.comperator !== undefined ? `:${group.comperator}` : ':=in='}`;

            // Handle dropdown-multiselect type
            if ('type' in group && group.type === 'dropdown-multiselect') {
                const selectedValues = derivedFilterState?.[itemKey] || [];
                newQuickFilterState[itemKey] = {
                    ...group,
                    selected: group.options.filter((opt) => selectedValues.includes(String(opt.value))),
                } as QuickFilterDropdownMultiselectForm;
            } else {
                // Handle checkbox group type (default)
                const checkboxGroup = group as {
                    selector: string;
                    comperator?: string;
                    filters: { argument: string; defaultSelected?: boolean }[];
                };
                newQuickFilterState[itemKey] = {
                    ...group,
                    filters: checkboxGroup.filters.map((filter, index) => ({
                        ...filter,
                        isSelected:
                            derivedFilterState?.[itemKey]?.includes(filter.argument) ?? filter.defaultSelected ?? false,
                        key: `qf-${itemKey}${filter.argument}${index}`,
                        value: filter.argument,
                    })),
                } as QuickFilterCheckboxGroupForm;
            }
        });

        return newQuickFilterState;
    };

    const getInitialMainFilters = (source: ParamSource = addressSource) => {
        const newMainFilterState = {} as MainFilterForm;
        const period = filterConfig?.periodQuickFilter;
        // Cleared filters leave the period empty; every other start fills it from the default preset.
        const periodDefault = source === 'none' ? undefined : periodPresetOf(period, period?.defaultPreset);
        const namedPeriod = periodPresetOf(period, readParam(source, PERIOD_PRESET_PARAM_KEY));

        const { derivedFilterState: mainFilterDerivedFilterState, filterArray } =
            getMainSearchDerivedFilterState(source);
        const quickFilterDerivedFilterState = getQuickSearchDerivedFilterState(source);

        // Get persisted isExcluding states (only when read from the URL)
        const customData = source === 'url' ? getCustomData<{ isExcluding?: Record<string, boolean> }>() : undefined;
        const persistedIsExcluding = customData?.isExcluding || {};

        const derivedFilterState = mergeDerivedFilterState(mainFilterDerivedFilterState, quickFilterDerivedFilterState);

        filterConfig?.filters?.forEach((group) => {
            const itemKey = getSelectorKey(group as MainFilterInForm);

            if (group.type === 'date' || group.type === 'offset-date-time') {
                newMainFilterState[itemKey] = {
                    ...group,
                    value: derivedFilterState?.[itemKey] ? derivedFilterState?.[itemKey][0] : null,
                };
            } else if (group.type === 'daterange') {
                const urlFrom = filterArray
                    .find((item) => item.includes(group.selector) && item.includes('=ge='))
                    ?.split('=ge=')?.[1];
                const urlTo = filterArray
                    .find((item) => item.includes(group.selector) && item.includes('=le='))
                    ?.split('=le=')?.[1];
                const isEmpty = filterArray.includes(`${group.selector}=isnull=true`);
                // A named preset is worked out from today, whatever dates were remembered with it. A period with a
                // default preset opens on it, also when the remembered filters hold no period.
                const preset =
                    namedPeriod?.selector === group.selector
                        ? namedPeriod.range(new Date())
                        : !urlFrom && !urlTo && !isEmpty && periodDefault?.selector === group.selector
                          ? periodDefault.range(new Date())
                          : undefined;
                // `parseISO` reads a `yyyy-MM-dd` bound as local midnight; `new Date` reads it as UTC, a day early west of UTC.
                const bound = (url: string | undefined, presetDay: Date | undefined) =>
                    presetDay ? presetDay.toISOString() : url ? parseISO(url).toISOString() : null;
                newMainFilterState[itemKey] = {
                    ...group,
                    from: bound(urlFrom, preset?.from),
                    to: bound(urlTo, preset?.to),
                    isEmpty,
                };
            } else if (group.type === 'offset-date-time-range') {
                if (group.comperator) {
                    const escapedComparator = group.comperator.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                    const pattern = new RegExp(`${group.selector}${escapedComparator}\\(([^)]+)\\)`);
                    const matchingItem = filterArray.find((item) => pattern.test(item));
                    const match = matchingItem?.match(pattern);
                    if (match) {
                        const parts = match[1].split(',');
                        const fromStr = parts[0]?.trim();
                        const toStr = parts[1]?.trim();
                        const isFromEmpty = !fromStr || fromStr === '""';
                        const isToEmpty = !toStr || toStr === '""';
                        newMainFilterState[itemKey] = {
                            ...group,
                            from: isFromEmpty ? null : parseISO(fromStr).toISOString(),
                            to: isToEmpty ? null : parseISO(toStr).toISOString(),
                        };
                    } else {
                        newMainFilterState[itemKey] = { ...group, from: null, to: null };
                    }
                } else {
                    const urlFrom = filterArray
                        .find((item) => item.includes(group.selector) && item.includes('=ge='))
                        ?.split('=ge=')?.[1];
                    const urlTo = filterArray
                        .find((item) => item.includes(group.selector) && item.includes('=le='))
                        ?.split('=le=')?.[1];
                    newMainFilterState[group.selector] = {
                        ...group,
                        from: urlFrom ? parseISO(urlFrom).toISOString() : null,
                        to: urlTo ? parseISO(urlTo).toISOString() : null,
                    };
                }
            } else if (group.type === 'interval') {
                if (group.comperator) {
                    const escapedComparator = group.comperator.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                    const pattern = new RegExp(`${group.selector}${escapedComparator}\\(([^)]+)\\)`);
                    const matchingItem = filterArray.find((item) => pattern.test(item));
                    const match = matchingItem?.match(pattern);
                    if (match) {
                        const parts = match[1].split(',');
                        const fromStr = parts[0]?.trim();
                        const toStr = parts[1]?.trim();
                        const isFromEmpty = !fromStr || fromStr === '""';
                        const isToEmpty = !toStr || toStr === '""';
                        newMainFilterState[itemKey] = {
                            ...group,
                            from: isFromEmpty ? null : Number(fromStr),
                            to: isToEmpty ? null : Number(toStr),
                        };
                    } else {
                        newMainFilterState[itemKey] = { ...group, from: null, to: null };
                    }
                } else {
                    const urlFrom = filterArray
                        .find((item) => item.includes(group.selector) && item.includes('=ge='))
                        ?.split('=ge=')?.[1];
                    const urlTo = filterArray
                        .find((item) => item.includes(group.selector) && item.includes('=le='))
                        ?.split('=le=')?.[1];
                    newMainFilterState[itemKey] = {
                        ...group,
                        from: urlFrom ? Number(urlFrom) : null,
                        to: urlTo ? Number(urlTo) : null,
                    };
                }
            } else if (group.type === 'number') {
                // `itemKey` already carries the comparator, so `=ge=10` and `=le=10` on one selector
                // restore into their own filters instead of overwriting each other.
                //
                // A URL is user-editable, so it is held to exactly what the input itself accepts: finite
                // (`isNumeric` lets "Infinity" through) and inside the configured bounds. Otherwise a
                // hand-edited `daysOverdue=ge=-1` would restore a threshold the field forbids and send it
                // on the next fetch.
                const rawValue = derivedFilterState?.[itemKey]?.[0];
                const parsedValue = rawValue !== undefined && isNumeric(rawValue) ? Number(rawValue) : NaN;
                const isWithinBounds =
                    Number.isFinite(parsedValue) &&
                    (group.min === undefined || parsedValue >= group.min) &&
                    (group.max === undefined || parsedValue <= group.max);
                newMainFilterState[itemKey] = {
                    ...group,
                    value: isWithinBounds ? parsedValue : null,
                };
            } else if (group.type === 'checkboxgroup') {
                const { no } = group;
                newMainFilterState[itemKey] = {
                    ...group,
                    filters: [
                        ...group.filters.map((filter, index) => ({
                            ...filter,
                            isSelected:
                                derivedFilterState?.[itemKey]?.includes(filter.argument) ??
                                filter.defaultSelected ??
                                false,
                            key: `mf-${itemKey}${filter.argument}${index}`,
                            value: filter.argument,
                        })),
                        ...(no
                            ? [
                                  {
                                      label: '',
                                      argument: no.argument,
                                      rsqlOverride: `${no.selector}${no.comparator}${no.argument}`,
                                      isSelected:
                                          derivedFilterState?.[`${no.selector}:${no.comparator}`]?.includes(
                                              no.argument
                                          ) ?? false,
                                      key: `mf-${itemKey}no`,
                                      value: no.argument,
                                      isNo: true,
                                  },
                              ]
                            : []),
                    ],
                };
            } else if (group.type === 'text') {
                // Anchor on selector + comperator: filters sharing a selector (several use `id`) would
                // otherwise restore each other's value.
                const comparator = group.comperator ?? '=lk=';
                // "Match none" sends the comparator's =not…= twin.
                const nonePrefix = `${group.selector}${notComparator(comparator)}`;
                const isNone = !!group.matchModes && filterArray.some((item) => item.startsWith(nonePrefix));
                const clausePrefix = isNone ? nonePrefix : `${group.selector}${comparator}`;
                const clauses = filterArray.filter((item) => item.startsWith(clausePrefix));
                // "Match all" sends one clause per term: read back as one list of terms.
                const rawValue =
                    group.matchModes && clauses.length > 1
                        ? `(${clauses.map((item) => item.slice(clausePrefix.length).replace(/^\(|\)$/g, '')).join(',')})`
                        : clauses[0]?.slice(clausePrefix.length);
                const emptyPrefix = `${group.selector}${group.emptyComparator}`;
                newMainFilterState[itemKey] = {
                    ...group,
                    ...(group.matchModes ? { match: isNone ? 'none' : clauses.length > 1 ? 'all' : 'any' } : {}),
                    isEmpty: filterArray.includes(
                        group.emptyComparator ? `${emptyPrefix}false` : emptyTextClause(group.selector)
                    ),
                    isNotEmpty: !!group.emptyComparator && filterArray.includes(`${emptyPrefix}true`),
                    value:
                        group.multiple === true
                            ? // Terms are double-quoted, so splitting on the quote-comma-quote boundary
                              // keeps a comma that belongs to a term. Only the outer quotes come off —
                              // stripping every quote would eat apostrophes inside a term.
                              (rawValue
                                  ?.replace(/^\(|\)$/g, '')
                                  .split('","')
                                  .map((term) => term.replace(/^"|"$/g, '')) ?? null)
                            : (rawValue?.replace(/^"|"$/g, '') ?? null),
                };
            } else if (group.type === 'multiselect') {
                const { values, isExcludingFromQuery } = getMultiselectFilterValues(group, itemKey, derivedFilterState);
                const hasPersistedValues = values.length > 0;
                const baseSelected = group.options.filter((option) => values.includes(String(option.value)));
                const selected =
                    emptinessOf(group, derivedFilterState) ??
                    (group.notEmptyOption && derivedFilterState?.[`${group.selector}:=nn=`]
                        ? [{ value: NOT_NULL_FILTER_SENTINEL, label: group.notEmptyOption.label }]
                        : (group.nullOption || group.conditions?.empty) && values.includes(NULL_FILTER_SENTINEL)
                          ? [{ value: NULL_FILTER_SENTINEL, label: group.nullOption?.label ?? '' }, ...baseSelected]
                          : baseSelected);

                newMainFilterState[itemKey] = {
                    ...group,
                    selected,
                    isExcluding:
                        isExcludingFromQuery || (hasPersistedValues ? persistedIsExcluding[itemKey] || false : false),
                };
            } else if (group.type === 'dynamicmultiselect') {
                const { values, isExcludingFromQuery } = getMultiselectFilterValues(group, itemKey, derivedFilterState);
                const hasPersistedValues = values.length > 0;

                const selected = values
                    .filter((id) => id !== NULL_FILTER_SENTINEL)
                    .map((id) => ({
                        value: isNumeric(id) ? Number(id) : id.startsWith('"') ? id : `"${id}"`,
                        label: '',
                    })) as unknown as SelectOption[];
                if ((group.nullOption || group.conditions?.empty) && values.includes(NULL_FILTER_SENTINEL)) {
                    selected.unshift({ value: NULL_FILTER_SENTINEL, label: group.nullOption?.label ?? '' });
                }
                newMainFilterState[itemKey] = {
                    ...group,
                    selected: emptinessOf(group, derivedFilterState) ?? selected,
                    isExcluding:
                        isExcludingFromQuery || (hasPersistedValues ? persistedIsExcluding[itemKey] || false : false),
                };
            } else if (group.type === 'composite') {
                const { values, isExcludingFromQuery } = getMultiselectFilterValues(group, itemKey, derivedFilterState);
                newMainFilterState[itemKey] = compositeFromArgs(group, values, isExcludingFromQuery);
            } else if (group.type === 'dropdown') {
                newMainFilterState[itemKey] = {
                    ...group,
                    selected:
                        group?.options?.find((option) =>
                            derivedFilterState?.[itemKey]?.includes(String(option.value))
                        ) ?? null,
                };
            } else if (group.type === 'dynamicdropdown') {
                const selected = (derivedFilterState?.[itemKey] || []).map((id) => ({
                    value: Number(id),
                    label: '',
                }))?.[0] as unknown as SelectOption;
                newMainFilterState[itemKey] = {
                    ...group,
                    selected: selected ?? null,
                };
            }
        });

        return newMainFilterState;
    };

    // Drops a sort whose key this list can't sort by (see `validSortAccessors`).
    const sanitizeSort = useCallback(
        (sort: string) => {
            if (!sort || !validSortAccessors) return sort;
            const [sortAccessor] = sort.split(',');
            return sortAccessor && validSortAccessors.includes(sortAccessor) ? sort : '';
        },
        [validSortAccessors]
    );

    const refetch = useCallback(
        async ({
            searchText,
            quickFilterState,
            mainFilterState,
            pageIndex = 0,
            sort: rawSort,
        }: {
            searchText: string;
            quickFilterState: QuickFilterForm;
            mainFilterState: MainFilterForm;
            pageIndex?: number;
            sort?: string;
        }) => {
            // A list must only ever send sort keys its own columns can emit. The API request, the
            // URL rewrite and the persisted filter entry below all derive from this one value, so
            // sanitizing here keeps a foreign sort key out of every downstream medium at once —
            // regardless of how it entered the state.
            const sort = sanitizeSort(rawSort ?? '');
            dropRuledOutOptions(mainFilterState, quickFilterState);
            const { searchParamObject, quickFilterParams, virtualMainFilterParams } = serializeFilterParams({
                searchText,
                quickFilterState,
                mainFilterState,
            });
            let filterUrl = '';

            //Stop, dont change the order of the actions below
            // The search clause rides along in the request filter but is carried in the URL by
            // `search` alone, so it is appended after `serializeFilterParams` built the `qf` param.
            if (searchText.length) {
                const searchRsql = buildRsqlFromSearchText(searchText, filterConfig?.searchBarSelectors ?? [], {
                    limitToFirstArgSelectors: filterConfig?.searchBarSelectorsLimitToFirstArg,
                    processComparator,
                });
                if (searchRsql) {
                    quickFilterParams.push(searchRsql);
                }
            }
            if (sort && sort.length) {
                searchParamObject['sort'] = sort;
            }
            const periodPreset = presetParamOf(filterConfig?.periodQuickFilter, mainFilterState, new Date());
            if (periodPreset) {
                searchParamObject[PERIOD_PRESET_PARAM_KEY] = periodPreset;
            }

            // Carry forward consumer-owned params (e.g. the My Portfolio scope/userIds) so they
            // survive this URL rewrite and get saved by filter persistence instead of being dropped
            // on every filter change.
            preservedKeys.forEach((key) => {
                const value = searchParams.get(key);
                if (value !== null) {
                    searchParamObject[key] = value;
                }
            });

            if (quickFilterParams.length || virtualMainFilterParams.length) {
                filterUrl = `${virtualMainFilterParams
                    .filter((item) => (item.startsWith(VIRTUAL_MARKER) ? item.slice(VIRTUAL_MARKER.length) : item))
                    .concat(quickFilterParams)
                    .join(';')}`;
            }

            const passiveUrl = filterConfig?.passiveFilter
                ? `${filterUrl.length ? ';' : ''}${filterConfig?.passiveFilter}`
                : '';

            const isPathnameStale = locationPathname !== mountedPathnameRef.current;

            if (
                !disableUrlState &&
                !isPathnameStale &&
                (!isEqual(Object.fromEntries(searchParams), searchParamObject) || !isInitialFetchDone)
            ) {
                setSearchParams(searchParamObject, { state: state, replace: !isInitialFetchDone });
            }
            if (!isEqual(virtualSearchParams, searchParamObject)) {
                setVirtualSearchParams(searchParamObject);
            }

            if (!disableUrlState && !isPathnameStale && isFilterPersistenceEnabled) {
                // Save isExcluding states for multiselect filters
                const isExcludingStates: Record<string, boolean> = {};
                Object.values(mainFilterState).forEach((filter) => {
                    if (filter.type === 'multiselect' || filter.type === 'dynamicmultiselect') {
                        const multiselectFilter = filter as MultiselectFilterForm;
                        const itemKey = getSelectorKey(multiselectFilter);
                        const hasSelection = Array.isArray(multiselectFilter.selected)
                            ? multiselectFilter.selected.length > 0
                            : false;
                        if (multiselectFilter.isExcluding && hasSelection) {
                            isExcludingStates[itemKey] = true;
                        }
                    }
                });
                // Save both filters and custom data in a single operation to avoid React batching issues
                saveFilters(searchParamObject, { isExcluding: isExcludingStates });
            }
            const newActiveFilter = filterUrl + passiveUrl;
            setActiveFilter(newActiveFilter);
            onFilterChange?.(newActiveFilter);
            if (!isInitialFetchDone) {
                setIsInitialFetchDone(true);
            }
            return await fetchData({
                filters: filterUrl + passiveUrl,
                page: pageIndex,
                pageSize: rowsPerPage,
                sort: composeSort ? composeSort(sort) : sort,
            }).unwrap();
        },
        [
            disableUrlState,
            fetchData,
            filterConfig?.passiveFilter,
            filterConfig?.periodQuickFilter,
            filterConfig?.searchBarSelectors,
            filterConfig?.searchBarSelectorsLimitToFirstArg,
            isFilterPersistenceEnabled,
            isInitialFetchDone,
            rowsPerPage,
            sanitizeSort,
            saveFilters,
            searchParams,
            setSearchParams,
            state,
            virtualSearchParams,
            onFilterChange,
            locationPathname,
            preservedKeys,
            composeSort,
        ]
    );

    const [searchBarState, setSearchBarState] = useState(() => getInitialParamString('search') ?? '');
    const [quickFilterState, setQuickFilterState] = useState(() => getInitialQuickFilters());
    const [mainFilterState, setMainFilterState] = useState(() => getInitialMainFilters());
    const [sortState, setSortState] = useState(() => sanitizeSort(getInitialParamString('sort') ?? ''));

    // Initialize missing localStorage entries with default structure
    useEffect(() => {
        initializeMissingEntry();
    }, [initializeMissingEntry]);

    /**
     * The initial fetch when page has been first open.
     * disableInitialFetch disables this automatic initial fetch - the first fetch will be executed
     * when user triggers actions like clearFilters, handleApplyFilters, handleNewSearch, handleLoadMore, etc.
     *
     * Uses a ref guard to ensure this only executes once, preventing double fetches caused by
     * state changes (like isInitialFetchDone) triggering effect re-runs.
     */
    useEffect(() => {
        if (!disableInitialFetch && !hasInitialFetchStartedRef.current && !disableFetch) {
            hasInitialFetchStartedRef.current = true;
            scrollTop();
            refetch({ searchText: searchBarState, quickFilterState, mainFilterState, sort: sortState }).then(() => {
                setCurrentPageIndex(0);
            });
        }
    }, [
        disableFetch,
        disableInitialFetch,
        mainFilterState,
        quickFilterState,
        refetch,
        scrollTop,
        searchBarState,
        searchParams,
        sortState,
    ]);

    /**
     * Handles "filterConfig", "rowsPerPage" and "composeSort" change.
     * Example 1: Project -> cases view, adding new case from a modal, changes ids in passiveFilter
     * Example 2: BankTransferConnectInvoicesModal -> adding invoice changes passiveFilter
     *
     * Note: Skip on first mount to prevent double fetch after initial fetch completes
     */
    useEffect(() => {
        // Skip on first mount to avoid double-fetching after initial fetch sets isInitialFetchDone
        if (isFirstMountRef.current) {
            isFirstMountRef.current = false;
            return;
        }

        if (isInitialFetchDone && !disableFetch) {
            const newMFState = getInitialMainFilters();
            setMainFilterState(newMFState);
            // `setCurrentPageIndex(0)` as well as the `pageIndex: 0` fetch, exactly as every other
            // reset path does: the refetch replaces the accumulation with page 0 alone, so a stale
            // index would make the next load-more ask for page N+1 and skip everything between.
            refetch({
                searchText: searchBarState,
                quickFilterState: quickFilterState,
                pageIndex: 0,
                mainFilterState: newMFState,
                sort: sortState,
            }).then(() => {
                setCurrentPageIndex(0);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterConfig, rowsPerPage, disableFetch, composeSort]);

    /**
     * Handles "searchParams" change (navigating back and forth and other browser url changes, nothing else should get here)
     * Other state changes should be handled using useListTableFilter return functions: clearFilters, handleApplyFilters and others.
     * When one of those is called, searchParams are updated along with the filter state, this useEffect will not pass through paramsEquals check
     */
    // TODO(react-doctor: no-cascading-set-state): table-filter hook coordinates
    // multiple independent state machines (search/sort/quick/main filters) in
    // response to URL changes — refactor would touch many call sites.
    useEffect(() => {
        // Skip if pathname changed — component is being navigated away from (e.g., tab switch)
        if (locationPathname !== mountedPathnameRef.current) {
            return;
        }
        // Preserved params (e.g. portfolio scope/userIds) ride in the URL but aren't part of the
        // filter-state comparison below, so a change to them alone wouldn't trip `paramsEquals`.
        // Track them here so such a change still refetches. Null on first run = no change.
        const preservedParamsSig = preservedKeys.map((key) => `${key}=${searchParams.get(key) ?? ''}`).join('&');
        const preservedParamsChanged =
            prevPreservedParamsRef.current !== null && preservedParamsSig !== prevPreservedParamsRef.current;
        prevPreservedParamsRef.current = preservedParamsSig;

        if (isInitialFetchDone && !disableFetch) {
            const filterParams = new URLSearchParams(searchParams);
            preservedKeys.forEach((key) => filterParams.delete(key));
            // The preset only names the dates `mf` already carries, and the comparison does not write it.
            filterParams.delete(PERIOD_PRESET_PARAM_KEY);
            const paramsEquals = compareSearchParams(filterParams, {
                searchText: searchBarState,
                quickFilterState,
                mainFilterState,
                sort: sortState,
            });
            if (!paramsEquals || preservedParamsChanged) {
                const newSbState = searchParams.get('search') ?? '';
                const newSortState = sanitizeSort(searchParams.get('sort') ?? '');
                const newQFState = getInitialQuickFilters();
                const newMFState = getInitialMainFilters();
                setSearchBarState(newSbState);
                setQuickFilterState(newQFState);
                setMainFilterState(newMFState);
                setSortState(newSortState);
                refetch({
                    searchText: newSbState,
                    quickFilterState: newQFState,
                    mainFilterState: newMFState,
                    sort: newSortState,
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disableFetch, searchParams, locationPathname]);

    const [paginationProps] = useMemo(() => {
        if (!data) return [undefined, undefined];
        const { content, ...rest } = data;
        return [{ ...rest }];
    }, [data]);

    const shouldFetchCount = paginationProps?.totalCountAccurate === false;

    const [totalsRowRequested, setTotalsRowRequested] = useState(defaultTotalsRowRequested);

    const shouldFetchTotals = onDemandTotals ? totalsRowRequested : forceTotalsVisible || mainFilterCount > 0; // auto-fetch only triggers when main filters have been set

    const [
        fetchElementCount,
        { data: _realTotalCount, isLoading: _isLoadingRealTotalCount, isFetching: _isFetchingRealTotalCount },
    ] = fetchCount?.() ?? [undefined, {}];

    const {
        data: totalsFromSeparateQuery,
        isLoading: _isLoadingTotals,
        isFetching: _isFetchingTotals,
    } = fetchTotals?.(
        { filters: activeFilter },
        {
            // `activeFilter` stays '' until the first fetch composes it from the URL and the passive
            // filter, so totals wait for that fetch: a ribbon on by default would otherwise sum the
            // caller's whole unfiltered set once before the list's own filter reaches it.
            // Auto-fetched totals also wait for a non-empty filter, but a forced ribbon must cover
            // the unfiltered list — some pages (e.g. bank transfers) have no passive filter, so
            // requiring a filter here would leave their always-visible ribbon permanently empty.
            skip:
                disableFetch ||
                !isInitialFetchDone ||
                (onDemandTotals
                    ? !shouldFetchTotals
                    : !shouldFetchTotals || (!forceTotalsVisible && activeFilter.length === 0)),
        }
    ) ?? {};

    useEffect(() => {
        if (isInitialFetchDone && shouldFetchCount && paginationProps.first === true) {
            fetchElementCount?.({ filters: activeFilter }, true);
        }
    }, [shouldFetchCount, activeFilter, paginationProps?.first, fetchElementCount, isInitialFetchDone]);

    const isLoadingRealTotalCount = _isLoadingRealTotalCount || _isFetchingRealTotalCount;
    const isLoadingTotals = _isLoadingTotals || _isFetchingTotals;

    // Some endpoints embed `totals` directly in the response envelope (e.g. /v1/expenses/grouped);
    // others expose it via a separate `fetchTotals` query. Cast to a structural `{ totals?: unknown }`
    // overlay so we don't require every TResponse to declare a totals field. The consumer narrows
    // the returned totals via its own `renderTotals` callback signature.
    const totalsFromData = (data as (TResponse & { totals?: unknown }) | undefined)?.totals;
    const totals = totalsFromData ?? totalsFromSeparateQuery;
    const shouldShowTotalsFromData = totalsFromData !== undefined;

    const realTotalCount = useMemo<number | undefined>(() => {
        if (isLoadingRealTotalCount || !shouldFetchCount || (shouldFetchCount && _realTotalCount === undefined)) {
            return undefined;
        }
        return _realTotalCount?.count;
    }, [_realTotalCount, isLoadingRealTotalCount, shouldFetchCount]);

    const handleApplyFilters = () => {
        onApplyFilterState?.(mainFilterState);
        refetch({ searchText: searchBarState, quickFilterState, mainFilterState, sort: sortState }).then(() => {
            setCurrentPageIndex(0);
        });
    };

    /**
     * Drops the search text and sets the filters to `source`'s: nothing (Clear) or the page's defaults (Reset).
     * Returns the filters it fetched with.
     */
    const restartFilters = (source: ParamSource) => {
        scrollTop();
        const initialMF = getInitialMainFilters(source);
        const initialQF = getInitialQuickFilters(source);
        const initialSearchBarText = '';
        setSearchBarState(initialSearchBarText);
        handleMainFilterStateChange(initialMF);
        handleQuickFilterStateChange(initialQF);
        setCurrentPageIndex(0);
        if (!disableUrlState && source === 'none') {
            // Clear both filters and custom data in a single operation to avoid React batching issues
            saveFilters({}, { isExcluding: {} });
        }
        onApplyFilterState?.(initialMF);
        refetch({
            searchText: initialSearchBarText,
            quickFilterState: initialQF,
            mainFilterState: initialMF,
            sort: sortState,
        });
        return { mainFilterState: initialMF, quickFilterState: initialQF };
    };

    const clearFilters = () => restartFilters('none');

    /**
     * A list whose period has a default preset can go back to how it opens: that preset and the page's other default
     * filters. Offered only while the applied filters differ from those.
     */
    const canResetFilters = !!filterConfig?.periodQuickFilter?.defaultPreset;
    const resetFilters = () => restartFilters('defaults');
    const isAtDefaultFilters = () => {
        const { searchParamObject } = serializeFilterParams({
            searchText: '',
            quickFilterState: getInitialQuickFilters('defaults'),
            mainFilterState: getInitialMainFilters('defaults'),
        });
        return (['mf', 'qf', 'search'] as const).every((key) => searchParamObject[key] === virtualSearchParams[key]);
    };
    const showsReset = canResetFilters && isInitialFetchDone && !isAtDefaultFilters();

    /**
     * Takes a whole new filter state and fetches with it — the filter bar's Apply and a chip's ×. The
     * bar edits copies rather than the live state the drawer writes into, so what it hands over is
     * final; the main filters still project onto the quick filters that share their keys.
     */
    const applyFilterState = (next: { mainFilterState: MainFilterForm; quickFilterState: QuickFilterForm }) => {
        scrollTop();
        handleQuickFilterStateChange(next.quickFilterState, false);
        handleMainFilterStateChange(next.mainFilterState);
        setCurrentPageIndex(0);
        onApplyFilterState?.(next.mainFilterState);
        refetch({ searchText: searchBarState, ...next, sort: sortState });
    };

    /**
     * Swaps the bare ids an entity picker comes back from the URL with (`label: ''`) for the options
     * its endpoint returned, dropping the ids it did not, without fetching the list again. Skipped
     * when the selection changed while the options were on their way.
     */
    const resolveSelectedOptions = useCallback(
        (key: string, requestedValues: SelectOption['value'][], options: SelectOption[]) => {
            setMainFilterState((prev) => {
                const filter = prev[key];
                if (filter?.type !== 'dynamicmultiselect') return prev;
                const sentinel = filter.selected.filter((option) => option.value === NULL_FILTER_SENTINEL);
                const values = filter.selected
                    .filter((option) => option.value !== NULL_FILTER_SENTINEL)
                    .map((option) => option.value);
                if (!isEqual(values, requestedValues)) return prev;
                return { ...prev, [key]: { ...filter, selected: [...sentinel, ...options] } };
            });
        },
        []
    );

    /** Returns the filters it fetched with, when it fetches. */
    const handleQuickFilterToggle = (
        value: boolean,
        groupSelector: string,
        key: string,
        immediateFetch: boolean = true
    ) => {
        const newQuickFilterState = quickFilterState;
        const filter = newQuickFilterState[groupSelector];
        if (!filter || !isCheckboxGroupQuickFilter(filter)) {
            return; // Only handle checkbox group type
        }
        changeCheckBoxGroupFilterState(key, value, filter);
        if (immediateFetch) {
            scrollTop();
        }
        const mainFiltersSynced: MainFilterForm = synchronizeMainFilterStatePure(quickFilterState, mainFilterState);
        if (immediateFetch) {
            refetch({
                searchText: searchBarState,
                quickFilterState: newQuickFilterState,
                mainFilterState: mainFiltersSynced,
                sort: sortState,
            }).then(() => {
                handleQuickFilterStateChange(newQuickFilterState, true);
                setCurrentPageIndex(0);
            });
            return { mainFilterState: mainFiltersSynced, quickFilterState: newQuickFilterState };
        }
    };

    /** Returns the filters it fetched with. */
    const handleQuickFilterDropdownChange = useCallback(
        (selected: SelectOption[], selector: string, comperator?: string) => {
            const itemKey = `${selector}:${comperator ?? '=in='}`;
            const newQuickFilterState = {
                ...quickFilterState,
                [itemKey]: {
                    ...quickFilterState[itemKey],
                    selected,
                } as QuickFilterDropdownMultiselectForm,
            };
            scrollTop();
            // A main filter on the same key owns the clause, so it has to carry this change before
            // the request is built — the state setter below would only land on the next tick.
            const mainFiltersSynced = synchronizeMainFilterStatePure(newQuickFilterState, mainFilterState);
            refetch({
                searchText: searchBarState,
                quickFilterState: newQuickFilterState,
                mainFilterState: mainFiltersSynced,
                sort: sortState,
            }).then(() => {
                handleQuickFilterStateChange(newQuickFilterState, true);
                setCurrentPageIndex(0);
            });
            return { mainFilterState: mainFiltersSynced, quickFilterState: newQuickFilterState };
        },
        [
            quickFilterState,
            searchBarState,
            mainFilterState,
            sortState,
            refetch,
            scrollTop,
            synchronizeMainFilterStatePure,
            handleQuickFilterStateChange,
        ]
    );

    const scrollAndRefetch = (searchText: string) => {
        scrollTop();
        warnIfKeywordsTruncated(searchText);
        if (!disableFetch) {
            refetch({ searchText, quickFilterState: quickFilterState, mainFilterState, sort: sortState }).then(() => {
                setCurrentPageIndex(0);
            });
        }
    };

    const handleLoadMore = useCallback(() => {
        if (data?.last) return;
        refetch({
            searchText: searchBarState,
            quickFilterState: quickFilterState,
            pageIndex: currentPageIndex + 1,
            mainFilterState,
            sort: sortState,
        }).then(() => {
            setCurrentPageIndex((prev) => prev + 1);
        });
        // `refetch` and `sortState` are load-bearing here, not tidiness: a stale `refetch` closes over
        // the sort composition of the previous render, so a page fetched after a sort or grouping
        // change would arrive ordered by the old one and split its groups across the boundary.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, currentPageIndex, searchBarState, quickFilterState, mainFilterState, refetch, sortState]);

    const handleNewSearch = useCallback(
        (searchText: string) => {
            warnIfKeywordsTruncated(searchText);
            refetch({
                searchText,
                quickFilterState: quickFilterState,
                pageIndex: 0,
                mainFilterState,
                sort: sortState,
            }).then(() => {
                setSearchBarState(searchText);
                setCurrentPageIndex(0);
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [quickFilterState, mainFilterState]
    );

    const handleSortChange = useCallback(
        (newSort: string) => {
            setSortState(newSort);
            refetch({
                searchText: searchBarState,
                quickFilterState,
                mainFilterState,
                pageIndex: 0,
                sort: newSort,
            }).then(() => {
                setCurrentPageIndex(0);
            });
        },
        [searchBarState, quickFilterState, mainFilterState, refetch]
    );

    return {
        activeFilter,
        dataToDisplay: data?.content || [],
        handleLoadMore,
        handleNewSearch,
        handleSortChange,
        sortState,
        isInitialFetchDone,
        clearFilters,
        resetFilters: showsReset ? resetFilters : undefined,
        handleApplyFilters,
        applyFilterState,
        resolveSelectedOptions,
        searchBarProps: {
            searchTextState: searchBarState,
            handleSearchBarChange: setSearchBarStateCallback,
            handleSearch: scrollAndRefetch,
        },
        quickFilterProps: {
            quickFilters: quickFilterState,
            handleChange: handleQuickFilterToggle,
            handleDropdownChange: handleQuickFilterDropdownChange,
            clearFilters,
            hasFilters,
        },
        mainFilterProps: {
            filters: mainFilterState,
            handleChange: handleMainFilterStateChange,
        },
        mainFilterCount,
        quickFilterCount,
        filterCount: mainFilterCount + quickFilterCount,
        isLoadingRealTotalCount: isLoadingRealTotalCount || (shouldFetchCount && _realTotalCount === undefined),
        realTotalCount,
        totals,
        // True when totals are (or will be) fetched for the current view — lets the layout
        // reserve the totals slot before the first response so it doesn't pop in (CLS).
        shouldFetchTotals,
        shouldShowTotals: shouldShowTotalsFromData || (shouldFetchTotals && totals !== undefined),
        // Drives the in-table totals row visibility. Opt-in via onDemandTotals (stays true while loading
        // so the row can show its spinner), or implicitly when the list response embeds `data.totals`.
        shouldShowTotalsRow: (onDemandTotals && shouldFetchTotals) || shouldShowTotalsFromData,
        isLoadingTotals: !!isLoadingTotals,
        onDemandTotals,
        totalsRowRequested,
        setTotalsRowRequested,
        totalCountForTotals: realTotalCount ?? paginationProps?.totalElements,
        filterPersistenceProps: {
            enabled: isFilterPersistenceEnabled,
            onChange: toggleFilterPersistence,
        },
    };
};

export { useListTableFilter };
